import axios from "axios";
import { getAccessTokenAsync } from "@kamstrup/flow-core/security";
import { getEnv } from "src/config/index";
import { getServices } from "src/logs";

const { logger } = getServices();

const baseURL =
  getEnv().REACT_APP_BACKEND_URL ||
  (() => {
    logger.logError({ message: "REACT_APP_BACKEND_URL environmental variable not set" });
    throw new Error("REACT_APP_BACKEND_URL environmental variable not set");
  })();

const identifyBackendUrl = (): string => {
  // Get the current URL
  const url = new URL(window.location.href);

  // Determine which BE URL to use
  let selectedBackendURL = "";
  if (url.hostname.includes(".flow.app.kamstrup.com")) {
    // use current domain and replace mfe with bff prefix
    selectedBackendURL = `${url.protocol}//${url.hostname.replace("mfe", "dashboardedge")}`;
  } else {
    selectedBackendURL = baseURL; // Use REACT_APP_BACKEND_URL
  }

  return selectedBackendURL;
};

const dashboardBFF = axios.create({
  baseURL: identifyBackendUrl()
});

dashboardBFF.interceptors.request.use(async (config) => {
  const scopes = [getEnv().REACT_APP_FLOW_ACCESS_TOKEN_SCOPE || ""];

  try {
    const accessToken = await getAccessTokenAsync(scopes);

    if (!accessToken) {
      logger.logWarning({ message: "DashboardBFF accessToken is empty" });
    }

    config.headers.Authorization = `Bearer ${accessToken}`;
  } catch (error) {
    // Log any errors related to getting the access token
    const tokenErrorMessage = "DashboardBFF Access Token Error";
    const scopeString = scopes.join(", ");
    if (error instanceof Error) {
      // Log any errors related to getting the access token
      logger.logError({
        message: tokenErrorMessage,
        properties: {
          name: error.name,
          message: error.message,
          stack: error.stack || "unknown stack",
          cause: String(error.cause ?? "unknown cause")
        }
      });
    } else if (error instanceof String) {
      logger.logError({ message: error.toString(), properties: { scopes: scopeString } });
    } else {
      logger.logError({
        message: `Unknown error type : ${tokenErrorMessage}`,
        properties: { scopes: scopeString }
      });
    }
  }

  return config;
});

dashboardBFF.interceptors.response.use(
  async (response) => {
    return response;
  },
  (error) => {
    const err = error as Error;
    logger.logError({
      message: "DashboardBFF - Exception",
      properties: {
        name: err.name,
        message: err.message,
        stack: err.stack || "unknown stack",
        cause: String(err.cause ?? "unknown cause")
      }
    });
    return Promise.reject(error);
  }
);

export { dashboardBFF };

